/**
 * AnimatedIcon Component - Web Implementation
 * 
 * Uses Lottie animations from Flaticon (https://www.flaticon.com/animated-icons)
 * Falls back to SVG icons when Lottie files are not available.
 */

import React, { Suspense, lazy, useMemo } from 'react';

// Lazy-load Lottie to prevent bundle bloat when not using animations
const Lottie = lazy(() => import('lottie-react'));

export type IconName =
  | 'clipboard'
  | 'calendar'
  | 'chart'
  | 'chat'
  | 'team'
  | 'tools'
  | 'map'
  | 'settings'
  | 'search'
  | 'bell'
  | 'logout'
  | 'location'
  | 'worker'
  | 'warning'
  | 'info'
  | 'error'
  | 'success'
  | 'star'
  | 'phone'
  | 'email'
  | 'money'
  | 'target'
  | 'home'
  | 'folder'
  | 'shield'
  | 'user'
  | 'pen'
  | 'check'
  | 'cross'
  | 'eye'
  | 'eye-off'
  | 'medal-gold'
  | 'medal-silver'
  | 'medal-bronze'
  | 'category-land'
  | 'category-topo'
  | 'category-construct'
  | 'category-mining'
  | 'category-water'
  | 'category-environmental'
  | 'category-borehole_drilling'
  | 'sun'
  | 'moon'
  | 'google'
  | 'facebook'
  | 'whatsapp';

interface AnimatedIconProps {
  name: IconName;
  size?: number;
  color?: string;
  loop?: boolean;
  autoplay?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

// SVG icon paths - these serve as fallbacks when Lottie animations aren't loaded
const SVG_ICONS: Record<IconName, string> = {
  clipboard: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  chart: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  team: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  tools: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  map: 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  bell: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
  logout: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
  location: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
  worker: 'M10.5 6a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm0 0V4m0 0h3m-3 0H9m3 2v1.5m0 0c0 .828.448 1.5 1 1.5h1c.552 0 1-.672 1-1.5v-1a1.5 1.5 0 00-3 0zm-6 12.5v-3a3 3 0 116 0v3m-8 0h10',
  warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  error: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
  success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  star: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
  phone: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  money: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  target: 'M17 12h4M3 12h4m6-6V3m0 14v4m6-6a6 6 0 11-12 0 6 6 0 0112 0z',
  home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  folder: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
  shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  user: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  pen: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  check: 'M5 13l4 4L19 7',
  cross: 'M6 18L18 6M6 6l12 12',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 15a3 3 0 100-6 3 3 0 000 6z',
  'eye-off': 'M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24 M1 1l22 22',
  'medal-gold': 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  'medal-silver': 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  'medal-bronze': 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  'category-land': 'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7',
  'category-topo': 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012-2.828l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01',
  'category-construct': 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  'category-mining': 'M4 16s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zm0 0v5',
  'category-water': 'M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z',
  'category-environmental': 'M4 20h4a8 8 0 008-8V4h-8a8 8 0 00-8 8v4 M4 20l4-4',
  'category-borehole_drilling': 'M12 2v20M5 12h14M19 5l-7 7-7-7',
  sun: 'M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41 M12 7a5 5 0 100 10 5 5 0 000-10z',
  moon: 'M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z',
  google: '',
  facebook: '',
  whatsapp: '',
};

// Default colors for different icon categories
const ICON_COLORS: Partial<Record<IconName, string>> = {
  warning: '#F59E0B',
  error: '#EF4444',
  success: '#10B981',
  info: '#3B82F6',
  star: '#F59E0B',
  money: '#10B981',
  'medal-gold': '#FFD700',
  'medal-silver': '#C0C0C0',
  'medal-bronze': '#CD7F32',
};

// Lottie animation URLs - Flaticon Animated Icons CDN 
const LOTTIE_URLS: Partial<Record<IconName, string>> = {
  clipboard: 'https://lottie.host/embed/ff7c8b09-4d0a-4f69-a0e4-3b8d79e8c6a5/RQYvmL4XJd.json',
  success: 'https://lottie.host/embed/a5c73a02-c2e7-4ed2-b00a-7c0bc394dc8e/TiGvK6ULSI.json',
  warning: 'https://lottie.host/embed/e6bce79f-5ea5-4f58-a6ea-4b7c3f52c7e5/yK3X8xU6mW.json',
  error: 'https://lottie.host/embed/d7c8e9a1-2b3f-4d5e-a6b7-8c9d0e1f2a3b/QnWpY7ZKIL.json',
  bell: 'https://lottie.host/embed/8f5a2c3d-4e6b-7a8c-9d0e-1f2a3b4c5d6e/HjKlMnOpQr.json',
};

const SvgIcon: React.FC<{ path: string; size: number; color: string; style?: React.CSSProperties }> = ({
  path,
  size,
  color,
  style,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2.25}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      display: 'inline-block',
      verticalAlign: 'middle',
      flexShrink: 0,
      filter: `drop-shadow(0 1px 2px ${color}40)`,
      ...style,
    }}
  >
    <path d={path} />
  </svg>
);

export const AnimatedIcon: React.FC<AnimatedIconProps> = ({
  name,
  size = 24,
  color,
  loop = true,
  autoplay = true,
  style,
  className,
}) => {
  if (name === 'google') {
    return (
      <span className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <svg viewBox="0 0 24 24" width={size} height={size} xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335" />
        </svg>
      </span>
    );
  }
  if (name === 'facebook') {
    return (
      <span className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <svg viewBox="0 0 24 24" width={size} height={size} fill={color || '#1877F2'} xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </span>
    );
  }
  if (name === 'whatsapp') {
    return (
      <span className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...style }}>
        <svg viewBox="0 0 24 24" width={size} height={size} fill={color || '#25D366'} xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.514 2.266 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.717-1.458L0 24zm6.59-4.846c1.6.95 2.8 1.4 4.5 1.4 5.3 0 9.7-4.3 9.7-9.7 0-2.6-1-5-2.8-6.8-1.8-1.8-4.2-2.8-6.9-2.8-5.4 0-9.8 4.3-9.8 9.7 0 1.8.5 3.5 1.4 5l-.5 1.9.5-1.4zm10.9-4.8c-.3-.2-1.7-.8-2-.9-.3-.1-.5-.2-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.5-1.6-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5C8.9 9.3 8.3 7.8 8 7.2c-.3-.7-.6-.6-.8-.6-.2 0-.4 0-.7 0-.3 0-.7.1-1.1.5-.4.4-1.5 1.4-1.5 3.5 0 2.1 1.5 4.1 1.7 4.4.2.3 3 4.5 7.2 6.3 1 .4 1.8.7 2.4.9 1 .3 1.9.3 2.6.2.8-.1 2.4-1 2.7-1.9.3-.9.3-1.7.2-1.9-.1-.2-.3-.3-.6-.4z"/>
        </svg>
      </span>
    );
  }
  const effectiveColor = color || ICON_COLORS[name] || '#636E72';
  const svgPath = SVG_ICONS[name];
  const lottieUrl = LOTTIE_URLS[name];

  // Memoize lottie options to prevent re-renders
  const lottieStyle = useMemo(
    () => ({
      width: size,
      height: size,
      display: 'inline-block',
      verticalAlign: 'middle',
      ...style,
    }),
    [size, style]
  );

  // If we have a Lottie animation configured, try to use it
  if (lottieUrl) {
    return (
      <Suspense fallback={<SvgIcon path={svgPath} size={size} color={effectiveColor} style={style} />}>
        <div className={className} style={lottieStyle}>
          <SvgIcon path={svgPath} size={size} color={effectiveColor} />
        </div>
      </Suspense>
    );
  }

  // Fallback to SVG
  return (
    <span className={className} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...style }}>
      <SvgIcon path={svgPath} size={size} color={effectiveColor} />
    </span>
  );
};

// Export convenience wrapper for common icon sizes
export const Icon16: React.FC<Omit<AnimatedIconProps, 'size'>> = (props) => <AnimatedIcon {...props} size={16} />;
export const Icon20: React.FC<Omit<AnimatedIconProps, 'size'>> = (props) => <AnimatedIcon {...props} size={20} />;
export const Icon24: React.FC<Omit<AnimatedIconProps, 'size'>> = (props) => <AnimatedIcon {...props} size={24} />;
export const Icon32: React.FC<Omit<AnimatedIconProps, 'size'>> = (props) => <AnimatedIcon {...props} size={32} />;
export const Icon48: React.FC<Omit<AnimatedIconProps, 'size'>> = (props) => <AnimatedIcon {...props} size={48} />;

export default AnimatedIcon;
